import React, { useMemo, useState, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTableStore } from './state/tableStore';
import { SocketContext } from './PokerTableApp';
import { getCardApiImageUrl } from './visual/cardApi';

export const useSocket = () => useContext(SocketContext);

// Toast notification system
const Toast: React.FC<{ message: string; onClose: () => void }> = ({ message, onClose }) => (
  <AnimatePresence>
    <motion.div
      initial={{ y: 40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 40, opacity: 0 }}
      transition={{ type: 'spring', stiffness: 180, damping: 18 }}
      style={{ position: 'fixed', bottom: 120, left: '50%', transform: 'translateX(-50%)', background: 'rgba(30,41,59,0.96)', color: '#fff', borderRadius: 12, padding: '16px 32px', fontWeight: 700, fontSize: 20, boxShadow: '0 2px 16px #0005', zIndex: 1000 }}
      onClick={onClose}
    >
      {message}
    </motion.div>
  </AnimatePresence>
);

export const PokerUIOverlay: React.FC = () => {
  const [toast, setToast] = useState<string | null>(null);
  const socket = useSocket();
  const {
    currentPlayerSeat,
    phase,
    seats,
    currentBet,
    lastRaiseAmount,
    remainingSeconds,
    actionTimeoutSeconds,
    mySeat,
    myHoleCards,
    pot,
    tableId,
    handResult,
    notification,
    setHandResult,
    setNotification,
    accountBalance,
    setAccountBalance,
    initialBuyIn,
    topUpUsed,
    addTopUpUsed,
    communityCards,
  } = useTableStore();
  const [raiseAmount, setRaiseAmount] = useState<number>(0);
  const [topUpAmount, setTopUpAmount] = useState<number>(1000);

  const mySeatState = useMemo(() => seats.find((seat) => seat.seat === mySeat), [seats, mySeat]);
  const myChips = mySeatState?.chips ?? 0;
  const myContribution = mySeatState?.currentContribution ?? 0;
  const myHandsPlayed = mySeatState?.handsPlayed ?? 0;
  const toCall = Math.max(0, currentBet - myContribution);
  const isMyTurn = mySeat >= 0 && currentPlayerSeat === mySeat;
  const minRaise = Math.max(lastRaiseAmount, 1);
  const canStandUp = mySeat >= 0 && myHandsPlayed >= 10;
  const statusMessage = !tableId
    ? 'Connecting...'
    : mySeat < 0
      ? 'Sit to play.'
      : isMyTurn
        ? 'Your turn.'
        : 'Waiting for other players.';
  const actionDisabledReason = mySeat < 0 ? 'You must be seated.' : !isMyTurn ? 'Not your turn.' : '';

  // Action handlers
  const handleAction = (action: 'fold' | 'call' | 'raise' | 'check', amount?: number) => {
    if (socket) {
      socket.send({ type: 'action', tableId: tableId ?? undefined, action, amount });
      setToast(`${action.charAt(0).toUpperCase() + action.slice(1)} sent!`);
    }
  };

  const topUpLimit = initialBuyIn ?? 0;
  const remainingTopUp = Math.max(0, topUpLimit - topUpUsed);

  const handleTopUp = () => {
    if (!socket || mySeat < 0) {
      setToast('You must be seated to top up.');
      return;
    }
    if (!topUpAmount || topUpAmount <= 0) {
      setToast('Enter a valid top-up amount.');
      return;
    }
    if (topUpLimit > 0 && topUpAmount > remainingTopUp) {
      setToast(`Top-up remaining is ${remainingTopUp.toLocaleString()} (100% of your buy-in total).`);
      return;
    }
    socket.send({ type: 'rebuy', tableId: tableId ?? undefined, amount: topUpAmount });
    if (topUpLimit > 0) addTopUpUsed(topUpAmount);
    setToast(`Top-up requested: $${topUpAmount.toLocaleString()}`);
    refreshBalance();
  };

  const handleStandUp = () => {
    if (!socket || mySeat < 0) {
      setToast('You must be seated to stand up.');
      return;
    }
    if (!canStandUp) {
      setToast('You must play at least 10 hands before standing up.');
      return;
    }
    socket.send({ type: 'stand_up', tableId: tableId ?? undefined });
    setToast('Stand up requested.');
    refreshBalance();
  };

  const refreshBalance = async () => {
    try {
      const res = await fetch('/api/poker/userinfo', { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      if (typeof data?.balance === 'number') {
        setAccountBalance(data.balance);
      }
    } catch (err) {
      // ignore
    }
  };

  const activeNotification = notification || toast;
  const winnersText = handResult
    ? handResult.pots
        .flatMap((potResult) =>
          potResult.winners.map((winner) => {
            const label = winner.seat === mySeat ? 'You' : `Seat ${winner.seat + 1}`;
            const hand = winner.hand ? ` (${winner.hand})` : '';
            return `${label}${hand} +$${winner.amount.toLocaleString()}`;
          })
        )
        .join(' • ')
    : '';
  const winnerSeats = new Set(handResult?.pots?.flatMap((pot) => pot.winners.map((winner) => winner.seat)) ?? []);

  const canRaise = isMyTurn && myChips > toCall && minRaise > 0;
  const raiseMax = Math.max(minRaise, myChips - toCall);
  const raiseValue = Math.min(Math.max(raiseAmount || minRaise, minRaise), raiseMax);

  React.useEffect(() => {
    if (!activeNotification) return;
    const timer = window.setTimeout(() => {
      if (notification) setNotification(null);
      setToast(null);
    }, 4000);
    return () => window.clearTimeout(timer);
  }, [activeNotification, notification, setNotification]);

  React.useEffect(() => {
    if (topUpLimit > 0) {
      setTopUpAmount((prev) => Math.min(prev || topUpLimit, remainingTopUp));
    }
  }, [topUpLimit, remainingTopUp]);

  return (
    <div style={{ position: 'absolute', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 2, pointerEvents: 'none' }}>
      {/* Action Controls */}
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 180, damping: 18 }}
        style={{ position: 'absolute', bottom: 32, left: '50%', transform: 'translateX(-50%)', pointerEvents: 'auto', background: 'rgba(30,41,59,0.92)', borderRadius: 16, padding: '18px 32px', boxShadow: '0 2px 16px #0005', display: 'flex', gap: 18 }}
      >
        <button
          onClick={() => handleAction('fold')}
          disabled={!isMyTurn}
          style={{ fontWeight: 700, fontSize: 20, borderRadius: 10, padding: '10px 28px', background: '#10B981', color: '#fff', border: 'none', marginRight: 8, opacity: isMyTurn ? 1 : 0.5 }}
        >
          Fold
        </button>
        {toCall === 0 ? (
          <button
            onClick={() => handleAction('check')}
            disabled={!isMyTurn}
            style={{ fontWeight: 700, fontSize: 20, borderRadius: 10, padding: '10px 28px', background: '#3B82F6', color: '#fff', border: 'none', marginRight: 8, opacity: isMyTurn ? 1 : 0.5 }}
          >
            Check
          </button>
        ) : (
          <button
            onClick={() => handleAction('call')}
            disabled={!isMyTurn}
            style={{ fontWeight: 700, fontSize: 20, borderRadius: 10, padding: '10px 28px', background: '#3B82F6', color: '#fff', border: 'none', marginRight: 8, opacity: isMyTurn ? 1 : 0.5 }}
          >
            Call {toCall > 0 ? `$${toCall.toLocaleString()}` : ''}
          </button>
        )}
        <button
          onClick={() => handleAction('raise', raiseValue)}
          disabled={!canRaise}
          style={{ fontWeight: 700, fontSize: 20, borderRadius: 10, padding: '10px 28px', background: '#F59E42', color: '#fff', border: 'none', opacity: canRaise ? 1 : 0.5 }}
        >
          Raise {canRaise ? `$${raiseValue.toLocaleString()}` : ''}
        </button>
        <input
          type="range"
          min={minRaise}
          max={raiseMax}
          value={raiseValue}
          onChange={(event) => setRaiseAmount(Number(event.target.value))}
          style={{ marginLeft: 18, width: 180 }}
          disabled={!canRaise}
        />
      </motion.div>
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.15, type: 'spring', stiffness: 180, damping: 18 }}
        style={{ position: 'absolute', bottom: 100, left: '50%', transform: 'translateX(-50%)', pointerEvents: 'auto', background: 'rgba(15,23,42,0.92)', borderRadius: 12, padding: '10px 16px', boxShadow: '0 2px 16px #0005', display: 'flex', gap: 10, alignItems: 'center' }}
      >
        <span style={{ color: '#cbd5f5', fontWeight: 700 }}>Top-up</span>
        <input
          type="number"
          min={1}
          max={topUpLimit > 0 ? remainingTopUp : undefined}
          value={topUpAmount}
          onChange={(event) => {
            const next = Number(event.target.value);
            if (topUpLimit > 0) {
              setTopUpAmount(Math.min(next, remainingTopUp));
            } else {
              setTopUpAmount(next);
            }
          }}
          style={{ width: 120, borderRadius: 8, border: '1px solid #334155', background: '#0b1324', color: '#fff', padding: '6px 10px' }}
        />
        <button
          onClick={handleTopUp}
          style={{ fontWeight: 700, borderRadius: 8, padding: '6px 14px', background: '#22c55e', color: '#0f172a', border: 'none' }}
        >
          Add Chips
        </button>
        <button
          onClick={handleStandUp}
          disabled={!canStandUp}
          style={{ fontWeight: 700, borderRadius: 8, padding: '6px 14px', background: canStandUp ? '#f97316' : '#475569', color: '#0f172a', border: 'none' }}
        >
          Stand Up
        </button>
      </motion.div>
      <div
        style={{
          position: 'absolute',
          bottom: 8,
          left: '50%',
          transform: 'translateX(-50%)',
          color: '#cbd5f5',
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        {actionDisabledReason || statusMessage}
      </div>
      {/* Player Stats HUD */}
      <motion.div
        initial={{ x: -40, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.2, type: 'spring', stiffness: 160, damping: 20 }}
        style={{ position: 'absolute', top: 32, left: 32, pointerEvents: 'auto', background: 'rgba(30,41,59,0.92)', borderRadius: 14, padding: '14px 28px', color: '#fff', fontWeight: 600, fontSize: 18, boxShadow: '0 2px 12px #0003' }}
      >
        <div>Player: <span style={{ color: '#a5b4fc' }}>You</span></div>
        <div>Chips: <span style={{ color: '#10B981' }}>${myChips.toLocaleString()}</span></div>
        <div>Seat: <span style={{ color: '#F59E42' }}>{mySeat >= 0 ? mySeat + 1 : '-'}</span></div>
        <div>Status: <span style={{ color: isMyTurn ? '#facc15' : '#94a3b8' }}>{statusMessage}</span></div>
        <div>Hands played: <span style={{ color: '#cbd5f5' }}>{myHandsPlayed}/10</span></div>
        <div>Balance: <span style={{ color: '#38bdf8' }}>{accountBalance !== null ? `${accountBalance} CIB` : '—'}</span></div>
      </motion.div>
      <motion.div
        initial={{ x: -40, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.25, type: 'spring', stiffness: 160, damping: 20 }}
        style={{ position: 'absolute', bottom: 140, left: 32, pointerEvents: 'auto', background: 'rgba(15,23,42,0.92)', borderRadius: 14, padding: '12px 20px', color: '#fff', fontWeight: 600, fontSize: 16, boxShadow: '0 2px 12px #0003' }}
      >
        <div style={{ marginBottom: 8, color: '#cbd5f5' }}>Your Cards</div>
        <div style={{ display: 'flex', gap: 12 }}>
          {(myHoleCards?.length ? myHoleCards : ['?', '?']).map((card, idx) => (
            <div key={`${card}-${idx}`} style={{ background: '#111827', borderRadius: 8, padding: 6, minWidth: 66, textAlign: 'center', fontWeight: 700 }}>
              {card === '?' ? (
                <span style={{ display: 'inline-block', padding: '12px 14px' }}>?</span>
              ) : (
                <img
                  src={getCardApiImageUrl(card)}
                  alt={card}
                  style={{ width: 60, height: 84, objectFit: 'contain', display: 'block' }}
                />
              )}
            </div>
          ))}
        </div>
      </motion.div>
      <motion.div
        initial={{ x: 40, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.28, type: 'spring', stiffness: 160, damping: 20 }}
        style={{ position: 'absolute', top: 160, right: 32, pointerEvents: 'auto', background: 'rgba(15,23,42,0.92)', borderRadius: 14, padding: '12px 20px', color: '#fff', fontWeight: 600, fontSize: 16, boxShadow: '0 2px 12px #0003', textAlign: 'right' }}
      >
        <div style={{ color: '#cbd5f5' }}>Pot</div>
        <div style={{ fontSize: 20, fontWeight: 800 }}>${pot.toLocaleString()}</div>
        <div style={{ marginTop: 6, color: '#94a3b8' }}>To Call: ${toCall.toLocaleString()}</div>
        <div style={{ color: '#94a3b8' }}>Current Bet: ${currentBet.toLocaleString()}</div>
      </motion.div>
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.25, type: 'spring', stiffness: 160, damping: 20 }}
        style={{ position: 'absolute', top: '48%', left: '47%', transform: 'translate(-50%, -50%)', pointerEvents: 'auto', background: 'rgba(15,23,42,0.92)', borderRadius: 14, padding: '12px 20px', color: '#fff', fontWeight: 600, fontSize: 16, boxShadow: '0 2px 12px #0003' }}
      >
        <div style={{ marginBottom: 8, color: '#cbd5f5', textAlign: 'center' }}>Community</div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          {[0, 1, 2, 3, 4].map((idx) => {
            const cardCode = communityCards?.[idx];
            return (
              <div key={`community-${idx}`} style={{ background: '#111827', borderRadius: 8, padding: 6, minWidth: 66, textAlign: 'center', fontWeight: 700 }}>
                {cardCode ? (
                  <img
                    src={getCardApiImageUrl(cardCode)}
                    alt={cardCode}
                    style={{ width: 60, height: 84, objectFit: 'contain', display: 'block' }}
                  />
                ) : (
                  <span style={{ display: 'inline-block', padding: '12px 14px', color: '#94a3b8' }}>?</span>
                )}
              </div>
            );
          })}
        </div>
      </motion.div>
      {/* Timer */}
      <motion.div
        initial={{ scale: 0.7, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.3, type: 'spring', stiffness: 200, damping: 16 }}
        style={{ position: 'absolute', top: 32, right: 32, pointerEvents: 'auto', background: 'rgba(30,41,59,0.92)', borderRadius: 14, padding: '14px 28px', color: '#fff', fontWeight: 700, fontSize: 22, boxShadow: '0 2px 12px #0003' }}
      >
        <span>⏳ {remainingSeconds || actionTimeoutSeconds || 0}s</span>
      </motion.div>
      <motion.div
        initial={{ x: 40, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ delay: 0.35, type: 'spring', stiffness: 160, damping: 20 }}
        style={{ position: 'absolute', bottom: 140, right: 32, pointerEvents: 'auto', background: 'rgba(15,23,42,0.92)', borderRadius: 14, padding: '12px 20px', color: '#fff', fontWeight: 600, fontSize: 14, boxShadow: '0 2px 12px #0003', minWidth: 200 }}
      >
        <div style={{ marginBottom: 8, color: '#cbd5f5' }}>Seats</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {seats.length === 0 && <span style={{ color: '#94a3b8' }}>Waiting for players...</span>}
          {seats.map((seat) => {
            const displayName = seat.displayName || seat.userId || `Seat ${seat.seat + 1}`;
            const label = seat.userId ? (seat.seat === mySeat ? 'You' : displayName) : `Seat ${seat.seat + 1}`;
            const isWinner = winnerSeats.has(seat.seat);
            return (
              <div key={seat.seat} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: isWinner ? '#facc15' : seat.seat === currentPlayerSeat ? '#38bdf8' : '#e2e8f0' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {seat.avatarUrl ? (
                    <img src={seat.avatarUrl} alt={label} style={{ width: 20, height: 20, borderRadius: '50%' }} />
                  ) : (
                    <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#334155', display: 'inline-grid', placeItems: 'center', fontSize: 10, fontWeight: 700 }}>
                      {String(label).slice(0, 1).toUpperCase()}
                    </span>
                  )}
                  {label}
                </span>
                <span>
                  {seat.userId ? `$${Number(seat.chips ?? 0).toLocaleString()}` : 'Empty'}
                  {seat.userId ? ` • ${Number(seat.handsPlayed ?? 0)}/10` : ''}
                </span>
              </div>
            );
          })}
        </div>
      </motion.div>
      {isMyTurn && (
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1, type: 'spring', stiffness: 200, damping: 16 }}
          style={{ position: 'absolute', top: 96, right: 32, pointerEvents: 'auto', background: '#facc15', borderRadius: 12, padding: '8px 14px', color: '#1e293b', fontWeight: 800, fontSize: 14, boxShadow: '0 2px 12px #0003' }}
        >
          Your turn
        </motion.div>
      )}
      {/* Toast Notification */}
      {activeNotification && (
        <Toast
          message={activeNotification}
          onClose={() => {
            if (notification) setNotification(null);
            setToast(null);
          }}
        />
      )}
      {handResult && (
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 16 }}
          style={{ position: 'absolute', top: 110, left: '50%', transform: 'translateX(-50%)', pointerEvents: 'auto', background: 'rgba(15,23,42,0.96)', borderRadius: 14, padding: '14px 24px', color: '#fff', fontWeight: 700, fontSize: 16, boxShadow: '0 2px 12px #0003' }}
        >
          <div style={{ marginBottom: 6 }}>Hand Complete</div>
          <div style={{ color: '#cbd5f5', fontWeight: 600 }}>{winnersText || 'No winners announced.'}</div>
          <button
            onClick={() => setHandResult(null)}
            style={{ marginTop: 10, fontWeight: 700, borderRadius: 8, padding: '6px 14px', background: '#38bdf8', color: '#0f172a', border: 'none' }}
          >
            Dismiss
          </button>
        </motion.div>
      )}
    </div>
  );
};
